import { Select, SelectProps } from 'antd';
import React, { useEffect, useState } from 'react';

export type OptionType = {
  label: string;
  value: string;
  key?: React.Key;
};

type CaseInsensitiveSelectProps = {
  options?: OptionType[];
} & Omit<SelectProps<string[]>, 'options'>;

export const toTitleCase = (input: string): string =>
  input
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const CaseInsensitiveSelect: React.FC<CaseInsensitiveSelectProps> = ({
  options = [],
  value = [],
  onChange,
  ...rest
}) => {
  const [availableOptions, setAvailableOptions] =
    useState<OptionType[]>(options);

  useEffect(() => {
    setAvailableOptions(options);
  }, [options]);

  const handleChange = (newValue: string[]) => {
    // Re-create the array of selected options based on the new values
    const valueCount: { [key: string]: number } = {};
    const processedOptions = newValue.map((val) => {
      // The value from AntD might already have our unique suffix, so we strip it
      // to get the original label for counting.
      const originalLabel = toTitleCase(val?.split('__')[0] ?? '');

      valueCount[originalLabel] = (valueCount[originalLabel] || 0) + 1;
      const count = valueCount[originalLabel] ?? 0;

      const uniqueValue =
        count > 1 ? `${originalLabel}__${count}` : originalLabel;

      return {
        label: originalLabel, // What the user sees
        value: uniqueValue, // What React/AntD uses as a key
      };
    });

    const newValues = processedOptions.map((opt) => opt.value);

    // Add any brand new tags to the dropdown list
    processedOptions.forEach((opt) => {
      const existsInOptions = availableOptions.some(
        (o) => o.value === opt.label,
      );
      if (!existsInOptions) {
        setAvailableOptions((prev) => [
          ...prev,
          { label: opt.label, value: opt.label },
        ]);
      }
    });

    onChange?.(newValues, processedOptions);
  };

  // We need to derive the options to render from the value prop,
  // because the values can be unique (e.g., "Kissing__2") but have a common label.
  const renderedOptions = (value ?? []).map((val) => {
    const label = val?.split('__')[0] ?? '';
    return { label, value: val };
  });

  return (
    <Select
      mode="tags"
      value={value}
      onChange={handleChange}
      // Pass both the original available options and the currently selected ones
      options={[...availableOptions, ...renderedOptions].filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value),
      )}
      {...rest}
    />
  );
};

export default CaseInsensitiveSelect;
